import React, { useState, useRef, useEffect, Suspense } from 'react';
import type { ChatMessage, MessagePart, PendingApproval, TablePart, TraceEvent } from './types';
import { parseMarkdownBlocks, tokenizeInline, type MdBlock } from './markdown';
import { humanizeEvent, summariseTrace, type RenderedStep } from './thinkingHumanizer';

// ---------- Inline token renderer ----------
//
// Tokenising lives in ./markdown (unit-tested); this file only maps tokens to
// elements: **bold**, *italic*, `code`, [text](url), bare URLs, MRN pills and
// blood-pressure badges.

function LinkAnchor({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#4338ca',
        textDecoration: 'underline',
        wordBreak: 'break-all',
      }}
    >
      {children}
    </a>
  );
}

function PatientIdPill({ text }: { text: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 8px',
        margin: '0 2px',
        borderRadius: 999,
        fontSize: '0.85em',
        fontWeight: 600,
        letterSpacing: '0.01em',
        backgroundColor: '#eef2ff',
        color: '#4338ca',
        border: '1px solid #e0e7ff',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
}

function BpBadge({ text }: { text: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 8px',
        margin: '0 2px',
        borderRadius: 6,
        fontSize: '0.85em',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        backgroundColor: '#ecfdf5',
        color: '#047857',
        border: '1px solid #d1fae5',
        whiteSpace: 'nowrap',
      }}
    >
      {text.replace(/\s+/g, '')}
    </span>
  );
}

function renderInline(text: string, keyPrefix = 'i'): React.ReactNode {
  if (!text) return null;
  return (
    <>
      {tokenizeInline(text).map((t, k) => {
        const key = `${keyPrefix}-${k}`;
        switch (t.kind) {
          case 'bold':
            return (
              <strong key={key} style={{ fontWeight: 600, color: '#0f172a' }}>
                {t.text}
              </strong>
            );
          case 'italic':
            return <em key={key}>{t.text}</em>;
          case 'code':
            return (
              <code
                key={key}
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.88em',
                  padding: '1px 5px',
                  borderRadius: 4,
                  backgroundColor: '#f1f5f9',
                  color: '#0f172a',
                }}
              >
                {t.text}
              </code>
            );
          case 'link':
            return (
              <LinkAnchor key={key} href={t.href}>
                {t.text}
              </LinkAnchor>
            );
          case 'url':
            return (
              <LinkAnchor key={key} href={t.href}>
                {t.href}
              </LinkAnchor>
            );
          case 'mrn':
            return <PatientIdPill key={key} text={t.text} />;
          case 'bp':
            return <BpBadge key={key} text={t.text} />;
          default:
            return <span key={key}>{t.text}</span>;
        }
      })}
    </>
  );
}

// ---------- Block-level markdown renderer ----------
//
// Parsing lives in ./markdown (see that file for the supported dialect).

// Strip a leading "🔴 " / "🟡 " / "🟢 " (or "🟣") from a heading so we can
// render the emoji as a real colored dot instead.
function extractHeadingDot(text: string): {
  color: string | null;
  rest: string;
} {
  const map: Record<string, string> = {
    '🔴': '#ef4444',
    '🟠': '#f97316',
    '🟡': '#eab308',
    '🟢': '#22c55e',
    '🔵': '#3b82f6',
    '🟣': '#a855f7',
  };
  const first = Array.from(text)[0];
  if (first && map[first]) {
    return { color: map[first], rest: text.slice(first.length).trim() };
  }
  return { color: null, rest: text };
}

function HeadingBlock({ block }: { block: Extract<MdBlock, { kind: 'heading' }> }) {
  const { color, rest } = extractHeadingDot(block.text);
  const isH2 = block.level === 2;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: isH2 ? 18 : 14,
        marginBottom: 8,
      }}
    >
      {color && (
        <span
          style={{
            width: isH2 ? 11 : 9,
            height: isH2 ? 11 : 9,
            borderRadius: '50%',
            backgroundColor: color,
            boxShadow: `0 0 0 3px ${color}26`,
            flexShrink: 0,
          }}
        />
      )}
      <span
        style={{
          fontSize: isH2 ? 15 : 13.5,
          fontWeight: 600,
          color: '#0f172a',
          letterSpacing: isH2 ? 0 : '0.01em',
          textTransform: isH2 ? 'none' : 'none',
        }}
      >
        {renderInline(rest, `h${block.level}`)}
      </span>
    </div>
  );
}

function BulletBlock({ block }: { block: Extract<MdBlock, { kind: 'bullet' }> }) {
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: '6px 0 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {block.items.map((item, idx) => (
        <li
          key={idx}
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            fontSize: 14,
            lineHeight: 1.55,
            color: '#334155',
          }}
        >
          <span
            style={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: '#94a3b8',
              marginTop: 9,
              flexShrink: 0,
            }}
          />
          <span style={{ flex: 1, minWidth: 0 }}>{renderInline(item, `li-${idx}`)}</span>
        </li>
      ))}
    </ul>
  );
}

function TableBlock({ block }: { block: Extract<MdBlock, { kind: 'table' }> }) {
  return (
    <div
      style={{
        overflowX: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        margin: '10px 0 12px',
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
          tableLayout: 'auto',
        }}
      >
        <thead>
          <tr>
            {block.headers.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: 'left',
                  padding: '9px 12px',
                  backgroundColor: '#f8fafc',
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  borderBottom: '1px solid #e2e8f0',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, ri) => (
            <tr
              key={ri}
              style={{
                borderBottom:
                  ri < block.rows.length - 1 ? '1px solid #f1f5f9' : 'none',
              }}
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: '10px 12px',
                    color: ci === 0 ? '#0f172a' : '#475569',
                    fontWeight: ci === 0 ? 500 : 400,
                    verticalAlign: 'top',
                    lineHeight: 1.45,
                  }}
                >
                  {renderInline(cell, `t-${ri}-${ci}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ParagraphBlock({ block }: { block: Extract<MdBlock, { kind: 'para' }> }) {
  return (
    <p
      style={{
        margin: '0 0 10px',
        fontSize: 14,
        lineHeight: 1.62,
        color: '#334155',
        wordBreak: 'break-word',
      }}
    >
      {renderInline(block.text, 'p')}
    </p>
  );
}

function OrderedBlock({ block }: { block: Extract<MdBlock, { kind: 'ordered' }> }) {
  return (
    <ol
      start={block.start}
      style={{ margin: '6px 0 10px', paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 5 }}
    >
      {block.items.map((item, idx) => (
        <li key={idx} style={{ fontSize: 14, lineHeight: 1.55, color: '#334155', paddingLeft: 2 }}>
          {renderInline(item, `ol-${idx}`)}
        </li>
      ))}
    </ol>
  );
}

function CodeBlock({ block }: { block: Extract<MdBlock, { kind: 'code' }> }) {
  return (
    <pre
      style={{
        margin: '8px 0 12px',
        padding: '10px 12px',
        borderRadius: 8,
        backgroundColor: '#0f172a',
        color: '#e2e8f0',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12.5,
        lineHeight: 1.5,
        overflowX: 'auto',
        whiteSpace: 'pre',
      }}
    >
      <code>{block.text}</code>
    </pre>
  );
}

function QuoteBlock({ block }: { block: Extract<MdBlock, { kind: 'quote' }> }) {
  return (
    <blockquote
      style={{
        margin: '6px 0 10px',
        padding: '4px 12px',
        borderLeft: '3px solid #cbd5e1',
        color: '#475569',
        fontSize: 14,
        lineHeight: 1.55,
      }}
    >
      {renderInline(block.text, 'q')}
    </blockquote>
  );
}

// While the answer is still streaming the model has only written the marker;
// the finished table arrives with the final envelope.
function TableRefPlaceholder() {
  return (
    <div
      style={{
        margin: '10px 0 12px',
        padding: '14px 12px',
        border: '1px dashed #cbd5e1',
        borderRadius: 10,
        color: '#64748b',
        fontSize: 13,
      }}
    >
      Preparing table…
    </div>
  );
}

function MarkdownBody({ content }: { content: string }) {
  const blocks = React.useMemo(() => parseMarkdownBlocks(content), [content]);
  return (
    <div>
      {blocks.map((block, idx) => {
        switch (block.kind) {
          case 'heading':
            return <HeadingBlock key={idx} block={block} />;
          case 'bullet':
            return <BulletBlock key={idx} block={block} />;
          case 'ordered':
            return <OrderedBlock key={idx} block={block} />;
          case 'table':
            return <TableBlock key={idx} block={block} />;
          case 'code':
            return <CodeBlock key={idx} block={block} />;
          case 'quote':
            return <QuoteBlock key={idx} block={block} />;
          case 'hr':
            return <hr key={idx} style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '12px 0' }} />;
          case 'tableRef':
            return <TableRefPlaceholder key={idx} />;
          case 'para':
            return <ParagraphBlock key={idx} block={block} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

// ---------- Structured parts (PHA-1155) ----------
const DataTablePart = React.lazy(() => import('./DataTablePart'));

function TablePartFallback({ part }: { part: TablePart }) {
  return (
    <TableBlock
      block={{
        kind: 'table',
        headers: part.columns,
        rows: part.rows.map((r) => r.map((c) => (c === null || c === undefined || c === '' ? '—' : String(c)))),
      }}
    />
  );
}

function PartsBody({ parts }: { parts: MessagePart[] }) {
  return (
    <div>
      {parts.map((p, idx) =>
        p.type === 'table' ? (
          <Suspense key={idx} fallback={<TablePartFallback part={p} />}>
            <DataTablePart part={p} />
          </Suspense>
        ) : (
          <MarkdownBody key={idx} content={p.text} />
        )
      )}
    </div>
  );
}

// ---------- Streaming-aware body wrapper ----------
function StreamedBody({
  content,
  isStreaming,
}: {
  content: string;
  isStreaming: boolean;
}) {
  const [visibleLen, setVisibleLen] = useState(isStreaming ? 0 : content.length);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isStreaming) {
      setVisibleLen(content.length);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    setVisibleLen(0);
    const total = content.length;
    const stepMs = 25;
    const charsPerStep = Math.max(1, Math.ceil(total / (1800 / stepMs)));
    intervalRef.current = window.setInterval(() => {
      setVisibleLen((prev) => {
        const next = prev + charsPerStep;
        if (next >= total) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return total;
        }
        return next;
      });
    }, stepMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [content, isStreaming]);

  const visibleText = content.slice(0, visibleLen);

  return (
    <div style={{ position: 'relative' }}>
      <MarkdownBody content={visibleText} />
      {isStreaming && visibleLen < content.length && (
        <span
          style={{
            display: 'inline-block',
            width: 2,
            height: '0.95em',
            backgroundColor: '#1976d2',
            marginLeft: 2,
            verticalAlign: 'text-bottom',
            animation: 'chat-blink 0.8s step-end infinite',
          }}
        />
      )}
    </div>
  );
}

// PHA-1155: text arriving live from the model. No typewriter -- the network is
// the typewriter -- just the markdown so far plus a blinking caret.
function LiveBody({ content }: { content: string }) {
  return (
    <div style={{ position: 'relative' }} aria-live="polite">
      <MarkdownBody content={content} />
      <span
        style={{
          display: 'inline-block',
          width: 2,
          height: '0.95em',
          backgroundColor: '#1976d2',
          marginLeft: 2,
          verticalAlign: 'text-bottom',
          animation: 'chat-blink 0.8s step-end infinite',
        }}
      />
    </div>
  );
}

// ---------- Tool row (Clarify-style, appears ABOVE the response text) ----------
// ---------- Thinking panel (MA-friendly orchestration trace) ----------
//
// Renders the agent's step-by-step reasoning as a collapsible list of plain-
// English lines. Tech detail (endpoint, HTTP status, durations) lives behind
// a per-step click so the clinician sees "Pulling up Jane's record" while a
// developer can still drill down to `POST /session/patient/details`.

const TONE_COLORS: Record<RenderedStep['tone'], string> = {
  thinking: '#64748b',
  doing: '#1d4ed8',
  ok: '#047857',
  warn: '#b45309',
  blocked: '#b91c1c',
  handoff: '#7c3aed',
  waiting: '#0284c7',
  secure: '#0f766e',
  done: '#334155',
};

function ThinkingStepRow({ step }: { step: RenderedStep }) {
  const [open, setOpen] = useState(false);
  const hasDetail = !!step.detail || typeof step.durationMs === 'number';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '4px 0',
        borderBottom: '1px dashed #eef2f7',
      }}
    >
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          padding: 0,
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          cursor: hasDetail ? 'pointer' : 'default',
          color: TONE_COLORS[step.tone] || '#475569',
          fontSize: 13,
          lineHeight: 1.45,
          width: '100%',
        }}
      >
        <span style={{ fontSize: 14, flexShrink: 0, width: 20, textAlign: 'center' }}>
          {step.icon}
        </span>
        <span style={{ flex: 1, wordBreak: 'break-word' }}>{step.summary}</span>
        {typeof step.durationMs === 'number' && step.durationMs >= 0 && (
          <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
            {formatDuration(step.durationMs)}
          </span>
        )}
      </button>
      {open && step.detail && (
        <div
          style={{
            marginLeft: 28,
            fontSize: 12,
            color: '#64748b',
            fontFamily:
              '"SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {step.detail}
        </div>
      )}
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function ThinkingPanel({ events }: { events: TraceEvent[] }) {
  // Auto-behavior: open while the turn is still in flight (no turn_end yet),
  // collapsed once the answer is in. The user's explicit click wins either
  // way — once they toggle, their choice sticks for the lifetime of the
  // message.
  const isStreaming =
    events.length > 0 && events[events.length - 1]?.kind !== 'turn_end';
  const [userChoice, setUserChoice] = useState<boolean | null>(null);
  const open = userChoice !== null ? userChoice : isStreaming;

  // Scroll the latest event into view while streaming. Lets the MA watch the
  // orchestration unfold without having to chase the bottom of the panel.
  const lastRowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (open && isStreaming && lastRowRef.current) {
      lastRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [events.length, open, isStreaming]);

  if (!events || events.length === 0) return null;
  const steps = events.map(humanizeEvent);
  const { count, totalMs } = summariseTrace(events);
  return (
    <div
      style={{
        marginBottom: 10,
        border: `1px solid ${isStreaming ? '#bae6fd' : '#e2e8f0'}`,
        borderRadius: 10,
        backgroundColor: isStreaming ? '#f0f9ff' : '#f8fafc',
        overflow: 'hidden',
        transition: 'border-color 200ms ease, background-color 200ms ease',
      }}
    >
      <button
        type="button"
        onClick={() => setUserChoice(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '8px 12px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#475569',
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: 0.15,
        }}
        aria-expanded={open}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12 }}>{open ? '▾' : '▸'}</span>
          <span>{isStreaming ? 'Working on it' : 'How I worked this out'}</span>
          <span style={{ fontWeight: 400, color: '#94a3b8' }}>
            · {count} step{count === 1 ? '' : 's'}
            {totalMs > 0 ? ` · ${formatDuration(totalMs)}` : ''}
          </span>
          {isStreaming && (
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: 999,
                backgroundColor: '#0ea5e9',
                animation: 'phanova-pulse 1s ease-in-out infinite',
              }}
            />
          )}
        </span>
        {!open && !isStreaming && (
          <span style={{ fontSize: 11, color: '#94a3b8' }}>Click to expand</span>
        )}
      </button>
      {open && (
        <div
          style={{
            padding: '0 12px 8px 12px',
            maxHeight: isStreaming ? 280 : 400,
            overflowY: 'auto',
          }}
        >
          {steps.map((s, i) => (
            <div key={i} ref={i === steps.length - 1 ? lastRowRef : null}>
              <ThinkingStepRow step={s} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const AGENT_BADGE_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  'chief-of-staff': { bg: '#e0f2fe', fg: '#0369a1', label: 'Chief of Staff' },
  onboarding: { bg: '#ede9fe', fg: '#6d28d9', label: 'Onboarding' },
  outreach: { bg: '#fef3c7', fg: '#b45309', label: 'Outreach' },
  triage: { bg: '#fee2e2', fg: '#b91c1c', label: 'Triage' },
  'care-plan': { bg: '#dcfce7', fg: '#047857', label: 'Care Plan' },
  billing: { bg: '#f1f5f9', fg: '#475569', label: 'Billing' },
  device: { bg: '#e0e7ff', fg: '#4338ca', label: 'Device' },
  'clinic-admin': { bg: '#fce7f3', fg: '#be185d', label: 'Admin' },
};

function AgentBadge({
  agent,
  subtle = false,
}: {
  agent?: string;
  subtle?: boolean;
}) {
  if (!agent) return null;
  const style = AGENT_BADGE_COLORS[agent];
  if (!style) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: subtle ? '1px 6px' : '2px 8px',
        borderRadius: 999,
        backgroundColor: style.bg,
        color: style.fg,
        fontSize: subtle ? 10 : 11,
        fontWeight: 500,
        letterSpacing: 0.1,
        lineHeight: 1.4,
      }}
    >
      {style.label}
    </span>
  );
}

function ToolRow({
  label,
  resolved,
  originAgent,
}: {
  label: string;
  resolved?: 'approved' | 'denied';
  originAgent?: string;
}) {
  const [hover, setHover] = useState(false);
  const resolvedColor =
    resolved === 'approved'
      ? '#059669'
      : resolved === 'denied'
      ? '#b91c1c'
      : hover
      ? '#334155'
      : '#64748b';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '3px 0',
        color: resolvedColor,
        fontSize: 13,
        fontWeight: 400,
        userSelect: 'none',
        cursor: 'default',
        transition: 'color 120ms ease',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 14 14"
        fill="none"
        style={{ flexShrink: 0, color: resolved ? resolvedColor : '#94a3b8' }}
      >
        <path
          d="M3 1.75C3 1.336 3.336 1 3.75 1H7.5l3.5 3.5V12.25c0 .414-.336.75-.75.75h-6.5A.75.75 0 0 1 3 12.25V1.75Z"
          stroke="currentColor"
          strokeWidth="1"
          fill="none"
        />
        <path d="M7.5 1v3.5H11" stroke="currentColor" strokeWidth="1" fill="none" />
      </svg>
      <span
        style={{
          textDecoration: 'underline',
          textDecorationColor: resolved ? resolvedColor : '#cbd5e1',
          textUnderlineOffset: 3,
          textDecorationThickness: 1,
        }}
      >
        {label}
      </span>
      {resolved && (
        <span style={{ fontSize: 11, fontWeight: 500, marginLeft: 4 }}>
          {resolved === 'approved' ? '· approved' : '· denied'}
        </span>
      )}
      {originAgent && originAgent !== 'chief-of-staff' && (
        <AgentBadge agent={originAgent} subtle />
      )}
    </div>
  );
}

// ---------- Approval card (appears in-line when a message has pendingApproval) ----------
function prettyBody(body: any): string {
  if (body == null) return '';
  try {
    const json = JSON.stringify(body, null, 2);
    return json.length > 500 ? json.slice(0, 500) + '…' : json;
  } catch {
    return String(body);
  }
}

function ApprovalCard({
  approval,
  onDecide,
  busy,
}: {
  approval: PendingApproval;
  onDecide: (approvalId: string, approved: boolean) => void;
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const isResolved = !!approval.resolved;
  const isWriteVerb = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(approval.method);

  // Visual tone by resolution state.
  const borderColor = isResolved
    ? approval.resolved === 'approved'
      ? '#86efac'
      : '#fecaca'
    : '#fde68a';
  const bgColor = isResolved
    ? approval.resolved === 'approved'
      ? '#f0fdf4'
      : '#fef2f2'
    : '#fffbeb';
  const iconColor = isResolved
    ? approval.resolved === 'approved'
      ? '#16a34a'
      : '#dc2626'
    : '#d97706';

  return (
    <div
      style={{
        marginTop: 12,
        marginBottom: 4,
        border: `1px solid ${borderColor}`,
        borderLeft: `3px solid ${iconColor}`,
        borderRadius: 10,
        padding: '12px 14px',
        backgroundColor: bgColor,
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          fontSize: 12,
          fontWeight: 600,
          color: iconColor,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
          {isResolved && approval.resolved === 'approved' ? (
            <path
              d="M4 10.5l4 4 8-8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : isResolved ? (
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          ) : (
            <>
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10 6v5M10 14h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </>
          )}
        </svg>
        {isResolved
          ? approval.resolved === 'approved'
            ? 'Action approved'
            : 'Action denied'
          : isWriteVerb
          ? 'Action requires your approval'
          : 'Confirmation required'}
      </div>

      {/* Endpoint + method */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          marginBottom: 8,
          fontVariantNumeric: 'tabular-nums',
          color: '#475569',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            padding: '1px 7px',
            borderRadius: 4,
            backgroundColor: '#fff',
            border: '1px solid #e2e8f0',
            color: '#334155',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.02em',
          }}
        >
          {approval.method}
        </span>
        <code
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            color: '#0f172a',
            backgroundColor: '#fff',
            padding: '1px 6px',
            borderRadius: 4,
            border: '1px solid #e2e8f0',
            wordBreak: 'break-all',
          }}
        >
          {approval.path}
        </code>
      </div>

      {/* Body preview toggle */}
      {approval.body && Object.keys(approval.body).length > 0 && (
        <div style={{ marginBottom: isResolved ? 0 : 10 }}>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              backgroundColor: '#fff',
              color: '#64748b',
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              style={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 150ms ease',
              }}
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {expanded ? 'Hide' : 'Show'} request body
          </button>
          {expanded && (
            <pre
              style={{
                marginTop: 8,
                padding: 10,
                borderRadius: 6,
                backgroundColor: '#0f172a',
                color: '#e2e8f0',
                fontSize: 11,
                lineHeight: 1.45,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                overflow: 'auto',
                maxHeight: 200,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {prettyBody(approval.body)}
            </pre>
          )}
        </div>
      )}

      {/* Action buttons */}
      {!isResolved && (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide(approval.id, true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 16px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#059669',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
              transition: 'background-color 120ms ease',
            }}
            onMouseEnter={(e) => {
              if (!busy) e.currentTarget.style.backgroundColor = '#047857';
            }}
            onMouseLeave={(e) => {
              if (!busy) e.currentTarget.style.backgroundColor = '#059669';
            }}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8.5l3.5 3.5L13 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Approve
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide(approval.id, false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              backgroundColor: '#fff',
              color: '#475569',
              fontSize: 13,
              fontWeight: 500,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
              transition: 'background-color 120ms ease, color 120ms ease',
            }}
            onMouseEnter={(e) => {
              if (!busy) {
                e.currentTarget.style.backgroundColor = '#f8fafc';
                e.currentTarget.style.color = '#0f172a';
              }
            }}
            onMouseLeave={(e) => {
              if (!busy) {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.color = '#475569';
              }
            }}
          >
            Deny
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Helpers ----------
function formatTimeAgo(timestamp?: string): string {
  if (!timestamp) return 'Just now';
  const delta = Date.now() - new Date(timestamp).getTime();
  if (delta < 60_000) return 'Just now';
  if (delta < 60_000 * 2) return '1 minute ago';
  if (delta < 60_000 * 60) return Math.floor(delta / 60_000) + ' minutes ago';
  if (delta < 60_000 * 60 * 2) return '1 hour ago';
  return Math.floor(delta / (60_000 * 60)) + ' hours ago';
}

// ---------- Message components ----------
// PHA-1155: clarifying question. Options are live only while this is the most
// recent question and nothing is running; afterwards the chosen answer (the
// next user message) stays highlighted so the thread reads back clearly.
function QuestionCard({
  question,
  answer,
  canAnswer,
  onAnswer,
}: {
  question: NonNullable<ChatMessage['question']>;
  answer?: string;
  canAnswer: boolean;
  onAnswer?: (text: string) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Clarifying question"
      style={{ margin: '4px 0 6px', display: 'flex', flexWrap: 'wrap', gap: 8 }}
    >
      {question.options.map((opt) => {
        const chosen = answer !== undefined && answer.trim() === opt.trim();
        const live = canAnswer && !!onAnswer;
        return (
          <button
            key={opt}
            type="button"
            disabled={!live}
            onClick={() => live && onAnswer!(opt)}
            aria-pressed={chosen}
            style={{
              padding: '7px 12px',
              borderRadius: 18,
              fontSize: 13,
              border: '1px solid ' + (chosen ? '#1d4ed8' : live ? '#bfdbfe' : '#e2e8f0'),
              backgroundColor: chosen ? '#eff6ff' : '#fff',
              color: chosen ? '#1d4ed8' : live ? '#1e40af' : '#94a3b8',
              cursor: live ? 'pointer' : 'default',
              fontWeight: chosen ? 600 : 500,
              textAlign: 'left',
            }}
          >
            {opt}
          </button>
        );
      })}
      {canAnswer && question.allowOther !== false && (
        <span style={{ width: '100%', fontSize: 12, color: '#94a3b8' }}>Or type a different answer below.</span>
      )}
    </div>
  );
}

function AssistantMessage({
  message,
  onDecideApproval,
  approvalBusy,
  onAnswer,
  canAnswer,
  answer,
}: {
  message: ChatMessage;
  onDecideApproval?: (approvalId: string, approved: boolean) => void;
  approvalBusy?: boolean;
  onAnswer?: (text: string) => void;
  canAnswer?: boolean;
  answer?: string;
}) {
  const hasTools = Array.isArray(message.toolUses) && message.toolUses.length > 0;
  const approval = message.pendingApproval;
  return (
    <div style={{ display: 'flex', gap: 14, maxWidth: '92%', minWidth: 0, marginTop: 20 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: '#e8f5e9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <span style={{ fontSize: 14 }}>🤖</span>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              {formatTimeAgo(message.timestamp)}
            </span>
            {message.agent && message.agent !== 'chief-of-staff' && (
              <AgentBadge agent={message.agent} />
            )}
          </span>
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 6px',
              borderRadius: 6,
              border: '1px solid transparent',
              backgroundColor: 'transparent',
              color: '#cbd5e1',
              fontSize: 12,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8fafc';
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.color = '#64748b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'transparent';
              e.currentTarget.style.color = '#cbd5e1';
            }}
            aria-label="Message actions"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="4" r="1.5" fill="currentColor" />
              <circle cx="8" cy="8" r="1.5" fill="currentColor" />
              <circle cx="8" cy="12" r="1.5" fill="currentColor" />
            </svg>
          </button>
        </div>

        {Array.isArray(message.traceEvents) && message.traceEvents.length > 0 && (
          <ThinkingPanel events={message.traceEvents} />
        )}

        {hasTools && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              marginBottom: 10,
            }}
          >
            {message.toolUses!.map((tool, i) => (
              <ToolRow
                key={i}
                label={tool.label}
                resolved={tool.resolved}
                originAgent={tool.originAgent}
              />
            ))}
          </div>
        )}

        {message.liveText ? (
          // PHA-1155: the model's text as it is being written.
          <LiveBody content={message.liveText} />
        ) : Array.isArray(message.parts) && message.parts.length > 0 ? (
          <PartsBody parts={message.parts} />
        ) : (
          (message.content || message.isStreaming !== false) && (
            <StreamedBody
              content={message.content}
              isStreaming={message.isStreaming ?? false}
            />
          )
        )}

        {message.question && message.question.options?.length > 0 && (
          <QuestionCard
            question={message.question}
            answer={answer}
            canAnswer={!!canAnswer}
            onAnswer={onAnswer}
          />
        )}

        {approval && onDecideApproval && (
          <ApprovalCard
            approval={approval}
            onDecide={onDecideApproval}
            busy={!!approvalBusy}
          />
        )}
      </div>
    </div>
  );
}

// PHA-1168: `#report-handle` in a user's own message shows as a subtle chip.
// The handle must start with a letter so "#1 priority" stays plain text.
const REPORT_TAG_RE = /(^|\s)(#[a-z][a-z0-9-]{1,59})(?=$|[\s.,;:!?)])/gi;

function renderUserText(text: string): React.ReactNode {
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(REPORT_TAG_RE.source, 'gi');
  while ((m = re.exec(text)) !== null) {
    const tagStart = m.index + m[1].length;
    if (tagStart > last) out.push(text.slice(last, tagStart));
    out.push(
      <span
        key={`tag-${tagStart}`}
        data-testid="report-tag"
        style={{
          padding: '0 6px',
          borderRadius: 6,
          backgroundColor: '#e0f2fe',
          color: '#0369a1',
          fontWeight: 600,
          fontSize: '0.92em',
        }}
      >
        {m[2]}
      </span>,
    );
    last = tagStart + m[2].length;
  }
  if (last === 0) return text;
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function UserMessage({ message }: { message: ChatMessage }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
      <div
        style={{
          maxWidth: '72%',
          padding: '10px 16px',
          borderRadius: '18px 18px 4px 18px',
          backgroundColor: '#f1f5f9',
          color: '#0f172a',
          fontSize: 14,
          lineHeight: 1.55,
          wordBreak: 'break-word',
        }}
      >
        {renderUserText(message.content)}
      </div>
    </div>
  );
}

function DateDivider({ label }: { label: string }) {
  return (
    <div style={{ textAlign: 'center', margin: '20px 0 12px' }}>
      <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{label}</span>
    </div>
  );
}

// PHA-549: inline system-style notice rendered when the chat scope changes
// mid-conversation (e.g. user switches patient tabs). Filtered out before
// being sent to the LLM in askAgent's historyToMessages — purely a UI signal.
function SystemNotice({ message }: { message: ChatMessage }) {
  return (
    <div style={{ textAlign: 'center', margin: '16px 0 8px' }}>
      <span
        style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: 999,
          background: '#f1f5f9',
          color: '#475569',
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        {message.content}
      </span>
    </div>
  );
}

export function ChatLog({
  messages,
  onDecideApproval,
  approvalBusy,
  wide,
  onAnswer,
}: {
  messages: ChatMessage[];
  onDecideApproval?: (approvalId: string, approved: boolean) => void;
  approvalBusy?: boolean;
  // PHA-1155: answer a clarifying question (sends the option as a message).
  onAnswer?: (text: string) => void;
  // PHA-1155: side-sheet / full-screen layouts get a centred reading column.
  wide?: boolean;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const last = messages[messages.length - 1];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, last?.content, last?.liveText]);

  // PHA-1071: render the whole thread, grouped by day. This used to filter to
  // `toDateString() === today`, which meant a conversation running across
  // midnight — or simply picked up the next morning — silently emptied itself
  // while the transcript was still in state and still being sent to the model
  // as history. The user saw a blank panel and the agent answered as if the
  // prior turns had happened, which is the worst of both.
  const dayLabel = (timestamp?: string) => {
    if (!timestamp) return 'Today';
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return 'Today';
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
    });
  };

  let lastLabel: string | null = null;

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px 8px' }}>
      <div style={wide ? { maxWidth: 980, margin: '0 auto' } : undefined}>
      {messages.filter((m) => !m.hidden).map((msg) => {
        const label = dayLabel(msg.timestamp);
        const divider = label !== lastLabel ? <DateDivider key={`d-${msg.id}`} label={label} /> : null;
        lastLabel = label;
        return (
          <React.Fragment key={`g-${msg.id}`}>
            {divider}
            {renderMessage(msg)}
          </React.Fragment>
        );
      })}
      <div ref={bottomRef} />
      </div>
    </div>
  );

  function renderMessage(msg: ChatMessage) {
    if (msg.role === 'system') {
      return <SystemNotice key={msg.id} message={msg} />;
    }
    if (msg.role === 'user') {
      return <UserMessage key={msg.id} message={msg} />;
    }
    // The user's reply to a question is the next user message after it.
    const idx = messages.indexOf(msg);
    const nextUser = messages.slice(idx + 1).find((m) => m.role === 'user');
    const isLastAssistant = !messages.slice(idx + 1).some((m) => m.role === 'assistant');
    return (
      <AssistantMessage
        key={msg.id}
        message={msg}
        onDecideApproval={onDecideApproval}
        approvalBusy={approvalBusy}
        onAnswer={onAnswer}
        canAnswer={isLastAssistant && !nextUser && !approvalBusy}
        answer={nextUser?.content}
      />
    );
  }
}
